import React, { useEffect } from "react";
import { useNavigate, useParams, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Supported languages
export const SUPPORTED_LANGUAGES = ["fr", "en", "ar", "es", "nl"];
export const DEFAULT_LANGUAGE = "fr";

// Language detector - redirects root "/" to the appropriate language
export const LanguageRedirect = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  useEffect(() => {
    // Detect browser language
    const browserLang = navigator.language?.split("-")[0] || DEFAULT_LANGUAGE;
    
    // Check if stored language preference exists
    const storedLang = localStorage.getItem("i18nextLng");
    
    // Determine the language to use
    let targetLang = storedLang || browserLang;
    
    // Fallback to default if not supported
    if (!SUPPORTED_LANGUAGES.includes(targetLang)) {
      targetLang = DEFAULT_LANGUAGE;
    }
    
    // Update i18n
    i18n.changeLanguage(targetLang);
    
    // Redirect to the language-prefixed URL
    navigate(`/${targetLang}/`, { replace: true });
  }, [navigate, i18n]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>
  );
};

// Language wrapper - syncs URL language with i18n
export const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGUAGES.includes(lang)) {
      // Update i18n if language changed
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
      }
      
      // Update document direction for RTL languages
      const dir = lang === "ar" ? "rtl" : "ltr";
      document.documentElement.dir = dir;
      document.documentElement.lang = lang;
    } else if (lang && !SUPPORTED_LANGUAGES.includes(lang)) {
      // Invalid language prefix - this could be an old URL without language prefix
      // Reconstruct the full original path and redirect with proper language prefix
      // e.g., /depart/ouarzazate should become /fr/depart/ouarzazate
      const fullPath = location.pathname; // This is the full path like /depart/ouarzazate
      const targetLang = localStorage.getItem("i18nextLng") || DEFAULT_LANGUAGE;
      
      // Redirect to the same path but with the language prefix
      navigate(`/${targetLang}${fullPath}`, { replace: true });
    }
  }, [lang, i18n, navigate, location.pathname]);

  // If no valid language, don't render (we're redirecting)
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
    return null;
  }

  return children || <Outlet />;
};

// Hook to get current language from URL
export const useCurrentLanguage = () => {
  const { lang } = useParams();
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
};

// Hook to generate localized path
export const useLocalizedPath = () => {
  const { lang } = useParams();
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  return (path) => {
    // Remove leading slash if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `/${currentLang}/${cleanPath}`;
  };
};

// Component to switch language
export const LanguageSwitcher = ({ targetLang }) => {
  const location = useLocation();
  const { lang } = useParams();
  
  // Get current path without language prefix
  const currentPath = location.pathname.replace(`/${lang}`, "") || "/";
  
  return `/${targetLang}${currentPath}`;
};

// Get localized URL for any language
export const getLocalizedUrl = (path, targetLang) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `/${targetLang}${cleanPath}`;
};
